// Chart-type "families" — the single source of truth for how every chart type is
// GROUPED and ICONED across the builder, the type picker, the gallery and the
// library. Promoted out of ChartTypeBar so the picker and gallery stay in sync.
//
// Each family owns a hue for its own CHROME: the icon is tinted in `fg`, sits on
// a soft `tile` background, and the selected state uses `ring`/`activeBg`. That
// is what makes the type picker scannable (the Power-BI-style coloured visual
// gallery), and it is where a family's colour stops.
//
// A FAMILY IS A CLASSIFICATION, NOT A PALETTE (AG-2106). None of these hues
// reaches a board: a tile is painted by the dashboard's theme, and where there
// is no theme by the app's own neutral series ramp (`styles/dataTokens`). The
// one exception is `palette`, and it is documented at its declaration.
import {
  Activity, AreaChart, BarChart3, BarChartHorizontal, CalendarDays, CandlestickChart,
  ChartColumnBig, ChartColumnStacked, CircleDot, Clock, Donut, Filter, Gauge, GitBranch,
  Goal, GitFork, Grid3x3, Hexagon, LayoutGrid, LineChart, ListOrdered, Map as MapIcon,
  MapPin, Megaphone, Network, PieChart, Radar, ScatterChart, Spline, SplitSquareHorizontal,
  SquareStack, Target, TrendingUp, Triangle, Waypoints, Workflow,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

export type FamilyKey =
  | 'comparison' | 'trend' | 'partToWhole' | 'distribution'
  | 'relationship' | 'financial' | 'geographic' | 'hierarchical' | 'flow' | 'advanced';

export interface ChartFamily {
  key: FamilyKey;
  title: string;
  /** icon / accent foreground */
  fg: string;
  /** soft tinted tile background (idle) */
  tile: string;
  /** stronger tint for the selected tile */
  activeBg: string;
  /** ring colour for the selected state */
  ring: string;
  /**
   * A categorical series palette anchored on this family's hue.
   *
   * A FAMILY DOES NOT COLOUR A DASHBOARD (AG-2106). This exists for exactly one
   * caller — `newChartDefaults.defaultProperties`, which reads the workspace's
   * "default colour family" PREFERENCE and seeds it onto a chart someone is
   * starting from scratch. That is a person's own choice about their own new
   * chart, made by name, and it is the only thing that makes the setting real.
   *
   * It is NOT what a tile on a board is painted with. A board's colours come
   * from its theme, which `_apply_theme_to_charts` stamps onto every tile; where
   * there is no theme the floor is the app's own neutral ramp
   * (`styles/dataTokens`). Nothing in `dashboard/` may import this module, and
   * `chartFamilyStaysOutOfTheBoard.test.ts` fails if anything starts to — because
   * "the chart is a donut, so the board is purple" is a design decision no chart
   * type is entitled to make.
   */
  palette: string[];
  types: string[];
}

// Ordered — drives section order in the gallery + "More" popover.
export const CHART_FAMILIES: ChartFamily[] = [
  {
    key: 'comparison', title: 'Comparison',
    fg: '#4F46E5', tile: '#EEF2FF', activeBg: '#E0E7FF', ring: '#6366F1',
    palette: ['#4f46e5', '#6366f1', '#818cf8', '#a5b4fc', '#3730a3', '#c7d2fe'],
    types: ['bar', 'column', 'grouped_bar', 'stacked_bar', 'lollipop'],
  },
  {
    key: 'trend', title: 'Trend',
    fg: '#059669', tile: '#ECFDF5', activeBg: '#D1FAE5', ring: '#10B981',
    palette: ['#059669', '#10b981', '#34d399', '#6ee7b7', '#047857', '#a7f3d0'],
    types: ['line', 'area', 'stacked_area', 'spline', 'step'],
  },
  {
    key: 'partToWhole', title: 'Part-to-Whole',
    fg: '#7C3AED', tile: '#F5F3FF', activeBg: '#EDE9FE', ring: '#8B5CF6',
    palette: ['#7c3aed', '#8b5cf6', '#a78bfa', '#c4b5fd', '#5b21b6', '#ddd6fe'],
    types: ['pie', 'donut', 'treemap', 'sunburst', 'waterfall'],
  },
  {
    key: 'distribution', title: 'Distribution',
    fg: '#D97706', tile: '#FFFBEB', activeBg: '#FEF3C7', ring: '#F59E0B',
    palette: ['#d97706', '#f59e0b', '#fbbf24', '#fcd34d', '#b45309', '#fde68a'],
    types: ['histogram', 'box_plot', 'violin', 'density', 'dot_plot'],
  },
  {
    key: 'relationship', title: 'Relationship',
    fg: '#0284C7', tile: '#F0F9FF', activeBg: '#E0F2FE', ring: '#0EA5E9',
    palette: ['#0284c7', '#0ea5e9', '#38bdf8', '#7dd3fc', '#075985', '#bae6fd'],
    types: ['scatter', 'bubble', 'heatmap', 'hexbin'],
  },
  {
    key: 'financial', title: 'Financial',
    fg: '#E11D48', tile: '#FFF1F2', activeBg: '#FFE4E6', ring: '#F43F5E',
    palette: ['#e11d48', '#f43f5e', '#fb7185', '#fda4af', '#9f1239', '#fecdd3'],
    types: ['candlestick', 'ohlc', 'stock_volume'],
  },
  {
    key: 'geographic', title: 'Geographic',
    fg: '#0D9488', tile: '#F0FDFA', activeBg: '#CCFBF1', ring: '#14B8A6',
    palette: ['#0d9488', '#14b8a6', '#2dd4bf', '#5eead4', '#115e59', '#99f6e4'],
    types: ['choropleth', 'symbol_map', 'flow_map'],
  },
  {
    key: 'hierarchical', title: 'Hierarchical',
    fg: '#C026D3', tile: '#FDF4FF', activeBg: '#FAE8FF', ring: '#D946EF',
    palette: ['#c026d3', '#d946ef', '#e879f9', '#f0abfc', '#86198f', '#f5d0fe'],
    types: ['dendrogram', 'org_chart'],
  },
  {
    key: 'flow', title: 'Process & Flow',
    fg: '#C026D3', tile: '#FDF4FF', activeBg: '#FAE8FF', ring: '#D946EF',
    palette: ['#c026d3', '#a855f7', '#8b5cf6', '#6366f1', '#7e22ce', '#e9d5ff'],
    types: ['sankey', 'funnel', 'flowchart', 'network', 'radar', 'gauge'],
  },
  {
    key: 'advanced', title: 'KPI & Advanced',
    fg: '#0891B2', tile: '#ECFEFF', activeBg: '#CFFAFE', ring: '#06B6D4',
    palette: ['#0891b2', '#06b6d4', '#22d3ee', '#67e8f9', '#0e7490', '#a5f3fc'],
    types: ['kpi', 'goal_progress', 'status_tile', 'segmented_progress', 'insight_banner', 'ranked_list', 'bullet', 'gantt', 'timeline', 'pareto', 'control', 'pyramid', 'marimekko', 'ridgeline_plot', 'calendar_heatmap'],
  },
];

// Per-type icon. Falls back to BarChart3 for unknown / user-authored types.
export const CHART_ICON: Record<string, LucideIcon> = {
  bar: BarChart3, column: ChartColumnBig, grouped_bar: BarChart3, stacked_bar: ChartColumnStacked, lollipop: CircleDot,
  line: LineChart, area: AreaChart, stacked_area: AreaChart, spline: Spline, step: Activity,
  pie: PieChart, donut: Donut, treemap: SquareStack, sunburst: Donut, waterfall: TrendingUp,
  histogram: BarChartHorizontal, box_plot: SquareStack, violin: Activity, density: AreaChart, dot_plot: CircleDot,
  scatter: ScatterChart, bubble: CircleDot, heatmap: Grid3x3, hexbin: Hexagon,
  candlestick: CandlestickChart, ohlc: CandlestickChart, stock_volume: BarChart3,
  choropleth: MapIcon, symbol_map: MapPin, flow_map: Waypoints,
  dendrogram: GitFork, org_chart: GitBranch,
  sankey: Workflow, funnel: Filter, flowchart: Workflow, network: Network, radar: Radar, gauge: Gauge,
  kpi: Target, goal_progress: Goal, status_tile: LayoutGrid, segmented_progress: SplitSquareHorizontal,
  insight_banner: Megaphone, ranked_list: ListOrdered, bullet: Target, gantt: BarChartHorizontal, timeline: Clock, pareto: TrendingUp, control: Activity,
  pyramid: Triangle, marimekko: LayoutGrid, ridgeline_plot: AreaChart, calendar_heatmap: CalendarDays,
};

// The default "primary" types shown inline in the picker (the rest live under
// the gallery). Order matters — most-common first.
export const PRIMARY_TYPES = [
  'kpi', 'goal_progress', 'status_tile', 'bar', 'column', 'line', 'area', 'pie', 'donut', 'scatter', 'bubble',
  'heatmap', 'funnel', 'treemap', 'stacked_bar', 'grouped_bar', 'waterfall', 'ranked_list',
];

const _TYPE_TO_FAMILY = new Map<string, ChartFamily>();
for (const fam of CHART_FAMILIES) for (const t of fam.types) _TYPE_TO_FAMILY.set(t, fam);

// Neutral family for user-authored / uncategorised types.
const CUSTOM_FAMILY: ChartFamily = {
  key: 'comparison', title: 'Custom',
  fg: '#475569', tile: '#F1F5F9', activeBg: '#E2E8F0', ring: '#64748B',
  palette: ['#4e79a7', '#59a14f', '#e1575a', '#f2a900', '#7b6fa6', '#3ba99c'],
  types: [],
};

/** Resolve the family (hue + icon context) for any chart type. */
export function familyForType(chartType: string): ChartFamily {
  return _TYPE_TO_FAMILY.get(chartType) ?? CUSTOM_FAMILY;
}

export function iconForType(chartType: string): LucideIcon {
  return CHART_ICON[chartType] ?? BarChart3;
}

/** The palette a family contributes to a new chart, or null for `auto`. */
export function paletteForFamily(key: string | undefined): string[] | null {
  if (!key || key === 'auto') return null;
  return CHART_FAMILIES.find((f) => f.key === key)?.palette ?? null;
}
